import {useMinSpin} from "../../hooks/useMinSpin";
import "./CasePage.scss";
import 'rsuite/useToaster/styles/index.css';
import 'rsuite/Message/styles/index.css';
import {CSSProperties, useEffect, useRef, useState} from "react";
import {Link} from "react-router-dom";
import axios from "axios";
import {PATHS_API} from "../../../enum";
import {PATHS} from "../../routers/utils/Paths.links";
import {useI18n} from "../../i18n/I18nProvider";
import type {TranslationKey} from "../../i18n/ua";
import {useUserStore} from "../../../modules/shop/user/store";
import {useServersStore} from "../../../modules/shop/servers/store";
import {CHILD_PROFILE_PATHS} from "../ProfilePage/components/Sidebar/Sidebar";
import ProductImage from "../../shared/ProductImage/ProductImage";
import ButtonLabel from "../../shared/ButtonLoader/ButtonLoader";
import Toaster, {ToasterProps} from "../../shared/Toaster/Toaster";
import {getCurrentDateInSeconds} from "../../shared/functions/getRandomId";
import Confetti from "../../shared/Confetti/Confetti";
import {CrateArt, HeroCrateArt, LockIcon} from "./CaseArt";

type Rarity = "legendary" | "epic" | "rare" | "uncommon" | "common";

interface ICaseItem {
    _id: string,
    name: string,
    imageUrl: string,
    price: number,
    type: string,
    rarity: Rarity,
    chance: number,
}

interface ICaseBox {
    price: number,
    items: ICaseItem[],
}

type Stage = "idle" | "opening" | "spinning" | "revealed";

// Ширина картки рулетки залежить від екрана (--reel-card у CasePage.scss), тож перед прокруткою її міряємо.
// Ці числа — лише запасний варіант, якщо виміряти не вдалося.
const CARD_WIDTH = 240;
const CARD_GAP = 10;
const REEL_LENGTH = 64;
const WIN_INDEX = 56;

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const SPIN_MS = () => reducedMotion() ? 1200 : 6500;

const formatChance = (chance: number) => {
    if (chance < 0.01) return "<0.01%";
    if (chance < 1) return `${chance.toFixed(2)}%`;
    return `${chance.toFixed(1)}%`;
};

// Стрічка рулетки лише для краси: справжній виграш вирішує сервер і ставиться на позицію WIN_INDEX
const buildReel = (items: ICaseItem[], winner: ICaseItem) => {
    const total = items.reduce((sum, i) => sum + i.chance, 0);
    const randomItem = () => {
        let roll = Math.random() * total;
        for (const item of items) {
            roll -= item.chance;
            if (roll <= 0) return item;
        }
        return items[items.length - 1];
    };

    const reel = Array.from({ length: REEL_LENGTH }, randomItem);
    // поруч із виграшем підкидаємо дорогі предмети — так зупинка виглядає напруженішою
    const rare = items.filter((i) => i.rarity === "legendary" || i.rarity === "epic");
    if (rare.length) {
        reel[WIN_INDEX - 1] = rare[Math.floor(Math.random() * rare.length)];
        reel[WIN_INDEX + 1] = rare[Math.floor(Math.random() * rare.length)];
    }
    reel[WIN_INDEX] = winner;
    return reel;
};

const RarityTag = ({ rarity, t }: { rarity: Rarity, t: (k: TranslationKey) => string }) => (
    <span className={`case-rarity-tag rarity-${rarity}`}>{t(`case.rarity.${rarity}` as TranslationKey)}</span>
);

const CasePage = () => {
    const { t } = useI18n();
    const user = useUserStore((s) => s.user);
    const fetchUser = useUserStore((s) => s.fetchUser);
    const servers = useServersStore((s) => s.servers);
    const fetchServers = useServersStore((s) => s.fetchServers);

    const [serverId, setServerId] = useState<string | null>(null);
    const [box, setBox] = useState<ICaseBox | null>(null);
    const [error, setError] = useState(false);
    const [stage, setStage] = useState<Stage>("idle");
    const [reel, setReel] = useState<ICaseItem[]>([]);
    const [winner, setWinner] = useState<ICaseItem | null>(null);
    // покупка, створена для виграного предмета: її можна одразу повернути за половину ціни
    const [wonPurchaseId, setWonPurchaseId] = useState<string | null>(null);
    const [returning, setReturning] = useState(false);
    // спінер видно щонайменше пів секунди (useMinSpin)
    const returnSpin = useMinSpin(returning);
    const [toastProps, setToastProps] = useState<ToasterProps | null>(null);

    const viewportRef = useRef<HTMLDivElement>(null);
    const stripRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!servers.length) fetchServers();
    }, []);

    // сервери, де адмін вимкнув ящик, не показуємо; за замовчуванням — перший зі списку
    const caseServers = servers.filter((s: any) => !s.lootboxDisabled);
    const activeServer = caseServers.find((s) => s._id === serverId) ?? caseServers[0];
    // сервери вже завантажились, а для ящика жодного не лишилось
    const noServers = servers.length > 0 && caseServers.length === 0;

    useEffect(() => {
        if (!activeServer) return;
        let cancelled = false;
        setBox(null);
        setError(false);

        axios.get(PATHS_API.LOOTBOX, { params: { server: activeServer._id } })
            .then(({ data }) => { if (!cancelled) setBox(data); })
            .catch((e) => {
                console.error("Помилка завантаження ящика:", e);
                if (!cancelled) setError(true);
            });

        return () => { cancelled = true; };
    }, [activeServer?._id]);

    // запуск прокрутки після того, як стрічка відрендерилась
    useEffect(() => {
        if (stage !== "spinning") return;
        const strip = stripRef.current;
        const viewport = viewportRef.current;
        if (!strip || !viewport) return;

        // справжній розмір картки й відступу на цьому екрані (на телефоні картки менші)
        const firstCard = strip.firstElementChild as HTMLElement | null;
        const cardWidth = firstCard?.offsetWidth || CARD_WIDTH;
        const gap = parseFloat(getComputedStyle(strip).columnGap) || CARD_GAP;
        const step = cardWidth + gap;

        const jitter = (Math.random() - 0.5) * cardWidth * 0.7;
        const target = WIN_INDEX * step + cardWidth / 2 - viewport.clientWidth / 2 + jitter;
        const duration = SPIN_MS();

        strip.style.transition = "none";
        strip.style.transform = "translate3d(0,0,0)";
        // примусовий reflow, щоб браузер «запам'ятав» стартову позицію перед анімацією
        void strip.offsetWidth;
        strip.style.transition = `transform ${duration}ms cubic-bezier(0.08, 0.62, 0.1, 1)`;
        strip.style.transform = `translate3d(${-target}px,0,0)`;

        // показуємо виграш, коли стрічка справді зупинилась; таймер — запасний варіант, якщо подія не прийде
        const reveal = () => window.setTimeout(() => setStage("revealed"), 250);
        let revealTimer = 0;
        const onEnd = (e: TransitionEvent) => {
            if (e.target === strip && e.propertyName === "transform") revealTimer = reveal();
        };
        strip.addEventListener("transitionend", onEnd);
        const fallback = window.setTimeout(() => setStage("revealed"), duration + 1500);

        return () => {
            strip.removeEventListener("transitionend", onEnd);
            window.clearTimeout(fallback);
            window.clearTimeout(revealTimer);
        };
    }, [stage, reel]);

    const skip = () => {
        const strip = stripRef.current;
        if (strip) {
            const current = strip.style.transform;
            strip.style.transition = "none";
            strip.style.transform = current;
        }
        setStage("revealed");
    };

    const balance = Number(user?.balance ?? 0);
    const isLoggedIn = !!user?.steamId;
    const canAfford = !!box && balance >= box.price;
    const busy = stage === "opening" || stage === "spinning";

    const openBox = async () => {
        if (!box || !activeServer || busy) return;
        setStage("opening");
        setWinner(null);

        try {
            const { data } = await axios.post(PATHS_API.LOOTBOX_OPEN, { serverId: activeServer._id }, { withCredentials: true });
            // баланс оновлюємо одразу з відповіді, без повторного запиту профілю
            useUserStore.setState((s) => ({ user: { ...s.user, balance: data.balance } }));
            setWinner(data.item);
            setWonPurchaseId(data.purchaseId ?? null);
            setReel(buildReel(box.items, data.item));
            setStage("spinning");
        } catch (e: any) {
            const message = e.response?.data?.message;
            setToastProps({ message: typeof message === "string" ? message : t("case.openError"), type: "error", id: getCurrentDateInSeconds() });
            setStage("idle");
        }
    };

    const closeReveal = () => { if (!returning) setStage("idle"); };

    // пріоритет і сет не повертаються (як і при звичайній покупці)
    const canReturn = !!wonPurchaseId && !!winner && winner.type !== "priority" && winner.type !== "set";
    const returnPrice = winner ? Math.floor(winner.price * 0.5) : 0;

    // повернення йде тим самим шляхом, що й у кошику: сервер виконує його із затримкою 1-5 секунд (для предметів з ящика)
    const returnWon = async () => {
        if (!wonPurchaseId || returning) return;
        setReturning(true);
        try {
            const { data } = await axios.put(PATHS_API.PURCHASES, { _id: wonPurchaseId }, { withCredentials: true });
            setToastProps({ message: data.message, type: data.status === 202 ? "info" : "success", id: getCurrentDateInSeconds() });
            setWonPurchaseId(null);
            setStage("idle");
        } catch (e: any) {
            const message = e.response?.data?.message;
            setToastProps({ message: typeof message === "string" ? message : t("case.returnError"), type: "error", id: getCurrentDateInSeconds() });
        } finally {
            setReturning(false);
            fetchUser();
        }
    };

    const confettiCount = winner?.rarity === "legendary" ? 64 : winner?.rarity === "epic" ? 44 : 28;

    return (
        <div className="containerContent case-page">
            <div className="case-hero">
                <div>
                    <h3 className="case-title">{t("case.title")}</h3>
                    <p className="case-subtitle">{t("case.subtitle")}</p>
                </div>
                <HeroCrateArt />
            </div>

            { caseServers.length > 1 &&
                <div className="case-servers" role="tablist" aria-label={t("case.server")}>
                    { caseServers.map((s) =>
                        <button
                            key={s._id}
                            type="button"
                            role="tab"
                            aria-selected={activeServer?._id === s._id}
                            disabled={busy}
                            className={`case-server ${activeServer?._id === s._id ? "case-server-active" : ""}`}
                            onClick={() => setServerId(s._id)}
                        >
                            {s.name}
                        </button>
                    )}
                </div>
            }

            <div className={`case-stage case-stage-${stage}`}>
                <div className="case-stage-glow" />
                <div className="case-dust" aria-hidden="true">
                    { Array.from({ length: 14 }, (_, i) => <span key={i} style={{ "--d": i } as CSSProperties} />) }
                </div>

                { stage === "idle" || stage === "opening"
                    ? <CrateArt className={stage === "opening" ? "case-crate-shaking" : ""} />
                    : <div className="case-reel" ref={viewportRef}>
                        <div className="case-reel-marker" />
                        <div className="case-reel-strip" ref={stripRef}>
                            { reel.map((item, i) =>
                                <div
                                    key={i}
                                    className={`case-reel-card rarity-${item.rarity} ${stage === "revealed" && i === WIN_INDEX ? "case-reel-winner" : ""}`}
                                >
                                    <ProductImage src={item.imageUrl} alt={item.name} />
                                    <span className="case-reel-name">{item.name}</span>
                                </div>
                            )}
                        </div>
                    </div>
                }

                <div className="case-actions">
                    { !box && !error && !noServers && <div className="case-skel case-skel-btn" /> }

                    { box && !isLoggedIn &&
                        <a className="case-open-btn" href={PATHS_API.LOGIN}>{t("case.login")}</a>
                    }

                    { box && isLoggedIn && stage === "spinning" &&
                        <button type="button" className="case-skip-btn" onClick={skip}>{t("case.skip")}</button>
                    }

                    { box && isLoggedIn && stage !== "spinning" && box.items.length > 0 && (
                        canAfford || busy
                            ? <button
                                type="button"
                                className={`case-open-btn ${stage === "opening" ? "btn-loading" : ""}`}
                                onClick={openBox}
                                disabled={busy}
                            >
                                <ButtonLabel loading={stage === "opening"}>
                                    <LockIcon />
                                    {t("case.open", { price: box.price })}
                                </ButtonLabel>
                            </button>
                            : <div className="case-no-money">
                                <span>{t("case.noMoney")} · {t("case.open", { price: box.price })}</span>
                                <Link className="case-open-btn" to={PATHS.DEPOSIT}>{t("case.deposit")}</Link>
                            </div>
                    )}
                </div>
                <p className="case-note">{t("case.noRefund")}</p>
            </div>

            <div className="case-contents-head">
                <h4>{t("case.contents")}</h4>
                { box && <span>{t("case.items", { n: box.items.length })}</span> }
            </div>

            { error && <div className="case-message">{t("case.error")}</div> }
            { noServers && <div className="case-message">{t("case.disabled")}</div> }

            { box && box.items.length === 0 && <div className="case-message">{t("case.empty")}</div> }

            <div className="case-grid">
                { !box && !error && !noServers && Array.from({ length: 10 }, (_, i) => <div key={i} className="case-item case-skel" />) }

                { box?.items.map((item, i) =>
                    <div key={item._id} className={`case-item rarity-${item.rarity}`} style={{ "--i": Math.min(i, 20) } as CSSProperties}>
                        <span className="case-item-chance" title={t("case.chance")}>{formatChance(item.chance)}</span>
                        <ProductImage className="case-item-img" src={item.imageUrl} alt={item.name} />
                        <div className="case-item-name" title={item.name}>{item.name}</div>
                        <div className="case-item-bottom">
                            <RarityTag rarity={item.rarity} t={t} />
                            <span className="case-item-price">{item.price} ₴</span>
                        </div>
                    </div>
                )}
            </div>

            { stage === "revealed" && winner &&
                <div className="case-reveal" onClick={closeReveal}>
                    <div className={`case-reveal-card rarity-${winner.rarity}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t("case.youWon")}>
                        <div className="case-reveal-rays" aria-hidden="true" />
                        <Confetti count={confettiCount} />
                        <div className="case-reveal-label">{t("case.youWon")}</div>
                        <ProductImage className="case-reveal-img" src={winner.imageUrl} alt={winner.name} />
                        <div className="case-reveal-name">{winner.name}</div>
                        <div className="case-reveal-meta">
                            <RarityTag rarity={winner.rarity} t={t} />
                            <span className="case-item-price">{winner.price} ₴</span>
                        </div>
                        <p className="case-reveal-text">
                            {t("case.inBasket", { server: activeServer?.name ?? "" })}
                        </p>
                        <div className="case-reveal-actions">
                            <Link className="case-ghost-btn" to={PATHS.PROFILE + CHILD_PROFILE_PATHS.BASKET}>{t("case.toBasket")}</Link>
                            { canReturn &&
                                <button
                                    type="button"
                                    className={`case-ghost-btn case-return-btn ${returnSpin ? "btn-loading" : ""}`}
                                    onClick={returnWon}
                                    disabled={returning}
                                    title={t("case.returnHint")}
                                >
                                    <ButtonLabel loading={returnSpin}>{t("case.return", { price: returnPrice })}</ButtonLabel>
                                </button>
                            }
                            { canAfford && !returning &&
                                <button type="button" className="case-open-btn" onClick={() => { closeReveal(); openBox(); }}>
                                    {t("case.again")}
                                </button>
                            }
                        </div>
                    </div>
                </div>
            }

            {toastProps && <Toaster message={toastProps.message} type={toastProps.type} id={toastProps.id} />}
        </div>
    );
};

export default CasePage;
